import './product-form-wrapper.component.scss';

import type { FunctionComponent } from 'preact';
import { h } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { Provider } from 'react-redux';

import { LoginButtons } from '../../../auth/components/login-buttons/login-buttons.component';
import { LoginType } from '../../../auth/models/auth.model';
import { selectAccountSettings, selectIsAuthenticated } from '../../../auth/store/auth.selectors';
import { checkAuthentication, loadAccountSettings } from '../../../auth/store/auth.thunk';
import { SpinnerComponent } from '../../../common/components/spinner/spinner.component';
import {
  ContentAvailabilityResponseType,
  DenialReason,
  GatedAccessResponseType,
} from '../../../content/models/access-response';
import {
  selectProductAccess,
  selectProductAccessLoading,
} from '../../../content/store/content.selectors';
import { loadProductAccess } from '../../../content/store/content.thunk';
import { ButtonService } from '../../../elements/button.service';
import { selectProduct } from '../../../elements/store/element.selectors';
import { loadProduct } from '../../../elements/store/element.thunk';
import { useTranslation } from '../../../i18n';
import type { Tier } from '../../../membership/models/membership.model';
import { ContractState } from '../../../membership/models/membership.model';
import { MembershipHelper } from '../../../membership/services/membership.helper';
import {
  selectMemberships,
  selectMembershipsLoading,
  selectMembershipUpdating,
} from '../../../membership/store/membership.selectors';
import { loadMemberships } from '../../../membership/store/membership.thunk';
import {
  getTermForTierAndProductVariant,
  getTierForProduct,
  isMembershipOrTierSoldOut,
} from '../../../membership/utils/membership.utlis';
import { NeedAccessButton } from '../../../need-access/components/need-access-button/need-access-button.component';
import { store } from '../../../store';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getCurrentUrl } from '../../../utils/window.utils';
import { useProductFormService } from '../../product-form-service.hook';

export interface ProductFormWrapperProps {
  productId: string;
  gatedProduct?: boolean;
  membershipProduct?: boolean;
  upsellProduct?: boolean;
  buttonFullWidth?: boolean;
}

export const ProductFormWrapperContent = ({
  productId,
  gatedProduct,
  membershipProduct,
  upsellProduct,
  buttonFullWidth,
}: ProductFormWrapperProps) => {
  // console.log('[ProductFormWrapper] Rendering with props:', {productId, gatedProduct, membershipProduct, upsellProduct, buttonFullWidth});
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const buttonService = new ButtonService();
  const productFormService = useProductFormService();

  // Refs
  const parentRef = useRef(null);
  const loadingRef = useRef(null);
  const contentRef = useRef(null);
  const defaultRef = useRef(null);
  const inStockRef = useRef(null);
  const loginRef = useRef(null);
  const soldOutRef = useRef(null);
  const alreadyPurchasedRef = useRef(null);
  const noAccessRef = useRef(null);
  const upgradeRef = useRef(null);
  const variantChangeHandlerRef = useRef<(() => void) | null>(null);

  // State
  const [accountSettings, setAccountSettings] = useState(null);
  const [loginTypes, setLoginTypes] = useState([]);
  const [membership, setMembership] = useState(null);
  const [tier, setTier] = useState(null);
  const [contract, setContract] = useState(null);
  const [variantId, setVariantId] = useState('');
  const [loginRedirectUrl, setLoginRedirectUrl] = useState('');

  // Form inputs
  const [variantInput, setVariantInput] = useState<HTMLElement | undefined>(null);
  const [sellingPlanInput, setSellingPlanInput] = useState(null);
  const [sellingPlanIdInput, setSellingPlanIdInput] = useState(null);
  const [sellingPlanNameInput, setSellingPlanNameInput] = useState(null);
  const [sellingPlanPriceInput, setSellingPlanPriceInput] = useState(null);
  const [sellingPlanIntervalCountInput, setSellingPlanIntervalCountInput] = useState(null);
  const [sellingPlanIntervalTypeInput, setSellingPlanIntervalTypeInput] = useState(null);

  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const accountSettingsResp = useAppSelector(selectAccountSettings);
  const membershipsLoading = useAppSelector(selectMembershipsLoading);
  const membershipsUpdating = useAppSelector(selectMembershipUpdating);
  const memberships = useAppSelector(selectMemberships);
  const product = useAppSelector(selectProduct);
  const productAccessLoading = useAppSelector(selectProductAccessLoading);
  const productAccess = useAppSelector(selectProductAccess);

  useEffect(() => {
    dispatch(checkAuthentication({ loginTypes: [LoginType.CUSTOMER] }));
    dispatch(loadMemberships());
    dispatch(loadProduct());
    dispatch(loadProductAccess(productId));
    if (gatedProduct) {
      dispatch(loadAccountSettings());
    }
  }, [dispatch]);

  useEffect(() => {
    const targetSection =
      gatedProduct || membershipProduct ? defaultRef.current : contentRef.current;
    migrateElementsIntoWrapper(targetSection);

    let cleanup: (() => void) | undefined;
    if (membershipProduct) {
      cleanup = processMembershipProduct(targetSection);
    } else {
      bindCartButtons(targetSection);
    }

    setLoginRedirectUrl(getCurrentUrl());
    buttonService.toggleVisibility(parentRef.current, true);

    // Return the cleanup function to clear the interval when component unmounts
    return cleanup;
  }, []);

  useEffect(() => {
    if (gatedProduct && productAccess) {
      dispatchGatedProductAccess();
    }
  }, [productAccess]);

  const migrateElementsIntoWrapper = (targetSection: any) => {
    // parentRef.current is the <section>
    // parentElement is the <single-product-form-wrapper-v2>, its parentElement is the <form>
    const wrapperElement = parentRef.current?.parentElement?.parentElement;
    if (!wrapperElement) return;

    Array.from(wrapperElement.children)
      .filter((el) => el !== parentRef.current?.parentElement)
      .forEach((child) => {
        targetSection?.appendChild(child);
        buttonService.toggleVisibility(child as HTMLElement, true);
      });
  };

  const bindCartButtons = (targetSection: any, tier?: Tier) => {
    productFormService.copyToDisabledButton(
      targetSection,
      soldOutRef.current,
      accountSettings?.productFormSoldOutButtonText,
      'Sold Out'
    );

    // Only add "Already Subscribed" button for membership products
    if (membershipProduct) {
      productFormService.addAlreadySubscribedButton(
        alreadyPurchasedRef.current,
        accountSettings?.productFormAlreadyPurchasedButtonText,
        'Already Subscribed'
      );
    }
    productFormService.bindAddToCart(targetSection, upsellProduct, membershipProduct, tier);
    productFormService.bindBuyItNow(targetSection, membershipProduct);
  };

  const dispatchGatedProductAccess = () => {
    // Reset all sections that may be stale from a previous access state
    buttonService.toggleVisibility(noAccessRef.current, false);
    buttonService.toggleVisibility(loginRef.current, false);
    buttonService.toggleVisibility(alreadyPurchasedRef.current, false);
    buttonService.toggleVisibility(defaultRef.current, false);

    buttonService.toggleVisibility(parentRef.current.parentElement, true);
    if (!productAccess) return;

    if (productAccess.availabilityResponse === ContentAvailabilityResponseType.UNRESTRICTED) {
      buttonService.toggleVisibility(inStockRef.current, true);
      buttonService.toggleVisibility(defaultRef.current, true);
    } else if (productAccess.availabilityResponse === ContentAvailabilityResponseType.GATED) {
      if (productAccess.accessResponse === GatedAccessResponseType.ACCESS_GRANTED) {
        buttonService.toggleVisibility(inStockRef.current, true);
        buttonService.toggleVisibility(defaultRef.current, true);
      } else if (productAccess.accessResponse === GatedAccessResponseType.REQUEST_LOGIN) {
        setLoginTypes(productAccess.loginTypes);
        buttonService.toggleVisibility(inStockRef.current, true);
        buttonService.toggleVisibility(loginRef.current, true);
      } else if (productAccess.accessResponse === GatedAccessResponseType.ACCESS_DENIED) {
        if (productAccess.denialReason === DenialReason.INSUFFICIENT_USES_AVAILABLE) {
          buttonService.toggleVisibility(alreadyPurchasedRef.current, true);
          productFormService.copyToDisabledButton(
            parentRef.current.parentElement,
            alreadyPurchasedRef.current,
            null,
            'Purchase Limit Reached'
          );
        } else {
          buttonService.toggleVisibility(noAccessRef.current, true);
        }
        setLoginTypes(productAccess.loginTypes);
        buttonService.toggleVisibility(inStockRef.current, true);
        buttonService.toggleVisibility(loginRef.current, true);
      }
    }
  };

  const buildHiddenInput = (name, id, form) => {
    let input: any = buttonService.getFirstElement(`[name="${name}"]`, form);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.id = id;
      input.name = name;
      form.appendChild(input);
    }
    return input;
  };

  const processMembershipProduct = (targetSection) => {
    if (!membershipProduct) return;

    const form = targetSection.closest('form');
    if (!form) return;

    dispatch(loadProduct());

    setSellingPlanInput(buildHiddenInput('selling_plan', 'sellingPlan', form));
    setSellingPlanIdInput(
      buildHiddenInput('properties[__selling_plan_id]', '__selling_plan_id', form)
    );
    setSellingPlanNameInput(
      buildHiddenInput('properties[__selling_plan_name]', '__selling_plan_name', form)
    );
    setSellingPlanPriceInput(
      buildHiddenInput('properties[__selling_plan_price]', '__selling_plan_price', form)
    );
    setSellingPlanIntervalCountInput(
      buildHiddenInput(
        'properties[__selling_plan_billing_interval_count]',
        '__selling_plan_billing_interval_count',
        form
      )
    );
    setSellingPlanIntervalTypeInput(
      buildHiddenInput(
        'properties[__selling_plan_billing_interval_type]',
        '__selling_plan_billing_interval_type',
        form
      )
    );

    const vInput = buttonService.getFirstElement('[name="id"]', form);
    setVariantInput(vInput);
    if (vInput) {
      setVariantId(vInput.value);
    }

    dispatch(loadMemberships());

    if (vInput && vInput.type === 'hidden') {
      const interval = setInterval(() => variantChangeHandlerRef.current?.(), 100);
      return () => clearInterval(interval);
    } else if (vInput) {
      vInput.addEventListener('change', () => variantChangeHandlerRef.current?.());
      return () => {}; // Event listener cleanup handled by component unmount
    }
  };

  const populateSellingPlan = () => {
    if (!product || !variantInput) return;

    const input = variantInput as HTMLInputElement;
    const variant = product.variants.find((v) => v.id.toString() === input.value);
    if (!variant?.selling_plan_allocations?.length) return;

    const sellingPlanId = variant.selling_plan_allocations[0].selling_plan_id;
    if (sellingPlanInput) sellingPlanInput.value = String(sellingPlanId);
    if (sellingPlanIdInput) sellingPlanIdInput.value = String(sellingPlanId);

    const sellingPlan = product.selling_plan_groups
      ?.flatMap((group) => group.selling_plans)
      ?.find((plan) => sellingPlanId === plan.id);

    if (sellingPlan && sellingPlanNameInput) {
      sellingPlanNameInput.value = sellingPlan.name;
    }

    if (variant && sellingPlanPriceInput) {
      sellingPlanPriceInput.value = String(variant.price / 100);
    }

    // Find the correct tier for the currently selected variant
    if (memberships && memberships.length > 0) {
      const currentTier = getTierForProduct(memberships, String(product.id), String(variant.id));
      if (currentTier) {
        const term = getTermForTierAndProductVariant(
          currentTier,
          String(product.id),
          String(variant.id)
        );
        if (term) {
          if (sellingPlanIntervalCountInput)
            sellingPlanIntervalCountInput.value = String(term.intervalCount);
          if (sellingPlanIntervalTypeInput)
            sellingPlanIntervalTypeInput.value = String(term.intervalType);
        }

        // Update the tier state to match the selected variant
        if (currentTier.id !== tier?.id) {
          setTier(currentTier);
        }
      }
    }
  };

  const handleMembershipButtons = (
    membershipData = membership,
    tierData = tier,
    contractData = contract
  ) => {
    if (!membershipData || !tierData) return;

    if (!gatedProduct) {
      buttonService.toggleVisibility(defaultRef.current, true);
    }

    // Use contract directly from parameters to avoid stale state
    const currentContract = contractData || membershipData?.contract;

    const soldOut = isMembershipOrTierSoldOut(membershipData, tierData);
    const contractActive = MembershipHelper.isTierContractActive(tierData, currentContract);
    const contractIsForThisTier = currentContract?.tier?.id === tierData?.id;
    const isUpgradeScenario =
      !soldOut &&
      tierData.contractState === ContractState.UPGRADE &&
      contractActive &&
      !contractIsForThisTier;
    const isDowngradeScenario =
      !soldOut &&
      tierData.contractState === ContractState.DOWNGRADE &&
      contractActive &&
      !contractIsForThisTier;
    const showAlreadySubscribed = (contractActive && contractIsForThisTier) || isDowngradeScenario;
    const showJoinButton = !soldOut && !contractActive && !isUpgradeScenario;
    const showUpgradeButton = isUpgradeScenario;

    buttonService.toggleVisibility(soldOutRef.current, soldOut);
    buttonService.toggleVisibility(alreadyPurchasedRef.current, showAlreadySubscribed);
    buttonService.toggleVisibility(inStockRef.current, showJoinButton);
    buttonService.toggleVisibility(upgradeRef.current, showUpgradeButton);

    // Add the upgrade button content if needed
    if (showUpgradeButton) {
      productFormService.addUpgradeButton(upgradeRef.current, tierData, membershipData);
    }
  };

  const handleMembershipVariantChange = () => {
    // Query the DOM directly to avoid stale closure issues
    let input: HTMLInputElement | null = null;

    // Method 1: From parentRef
    const wrapperEl = parentRef.current?.parentElement?.parentElement;
    if (wrapperEl) {
      input = wrapperEl.querySelector('[name="id"]') as HTMLInputElement;
    }

    // Method 2: Use buttonService (fallback)
    if (!input && parentRef.current) {
      const formFromParent = parentRef.current.closest('form');
      if (formFromParent) {
        input = buttonService.getFirstElement('[name="id"]', formFromParent) as HTMLInputElement;
      }
    }

    // Only populate if variant actually changed to avoid unnecessary updates
    if (input && variantId !== input.value) {
      populateSellingPlan();
      setVariantId(input.value);

      // Reload memberships if needed
      if (MembershipHelper.isMembershipOrTierMaxMembersSpecified(membership, tier)) {
        dispatch(loadMemberships());
      }
    }
  };

  // Keep the ref updated with the latest handler
  useEffect(() => {
    variantChangeHandlerRef.current = handleMembershipVariantChange;
  });

  const resetMembershipSections = () => {
    buttonService.toggleVisibility(soldOutRef.current, false);
    buttonService.toggleVisibility(alreadyPurchasedRef.current, false);
    buttonService.toggleVisibility(inStockRef.current, false);
    buttonService.toggleVisibility(upgradeRef.current, false);
  };

  useEffect(() => {
    populateSellingPlan();
  }, [product]);

  useEffect(() => {
    if (!memberships?.length || !membershipProduct) return;

    memberships.forEach((m) => {
      m.tiers
        .filter((t) => {
          return t.terms.some((term) =>
            term.associatedProductVariants.some((variant) => variant.sourceProductId === productId)
          );
        })
        .forEach((tier) => {
          setMembership(m);
          setTier(tier);
          setContract(m.contract);
          setAccountSettings(m?.accountSettings);
          bindCartButtons(
            gatedProduct || membershipProduct ? defaultRef.current : contentRef.current,
            tier
          );
          populateSellingPlan();
        });
    });
  }, [memberships, productId]);

  // Handle button visibility when memberships data changes
  useEffect(() => {
    if (membershipProduct && !membershipsUpdating && !membershipsLoading && memberships?.length) {
      // Find the current membership/tier directly from Redux state to avoid stale local state
      const currentMembership = memberships.find((m) =>
        m.tiers.some((t) =>
          t.terms.some((term) =>
            term.associatedProductVariants.some((variant) => variant.sourceProductId === productId)
          )
        )
      );

      const currentTier = currentMembership?.tiers.find((t) =>
        t.terms.some((term) =>
          term.associatedProductVariants.some((variant) => variant.sourceProductId === productId)
        )
      );

      if (currentMembership && currentTier) {
        // Update local state for other uses
        setMembership(currentMembership);
        setTier(currentTier);
        setContract(currentMembership.contract);

        // Pass fresh data directly to avoid stale state
        handleMembershipButtons(currentMembership, currentTier, currentMembership.contract);
      }
    }
  }, [memberships, membershipsUpdating, membershipsLoading]);

  // Hide button sections while memberships are updating or loading
  useEffect(() => {
    if (membershipsUpdating || membershipsLoading) {
      buttonService.toggleVisibility(soldOutRef.current, false);
      buttonService.toggleVisibility(alreadyPurchasedRef.current, false);
      buttonService.toggleVisibility(inStockRef.current, false);
      buttonService.toggleVisibility(upgradeRef.current, false);
      buttonService.toggleVisibility(noAccessRef.current, false);
    }
  }, [membershipsUpdating, membershipsLoading]);

  const isLoading = membershipsLoading || productAccessLoading || membershipsUpdating;

  return (
    <section
      ref={parentRef}
      class={`single-hidden single-buy-buttons ${buttonFullWidth ? 'single-btn-full-width' : ''}`}
      hidden
    >
      {/* Loading Section */}
      <section class={!isLoading ? 'single-hidden' : ''} ref={loadingRef} hidden={!isLoading}>
        <button class="btn button" disabled style={{ cursor: 'not-allowed' }}>
          <SpinnerComponent size="small" />
        </button>
      </section>

      {/* No Access Section */}
      <section class="single-hidden single-need-access-section" hidden ref={noAccessRef}>
        <div>
          <NeedAccessButton
            message={t('access.joinForAccess')}
            contentType="PRODUCT"
            contentId={productId}
            showLock={false}
          />
        </div>
      </section>

      {/* In Stock Section */}
      <section ref={inStockRef}>
        {/*<ProductFormWallet />*/}

        <section class="single-gate-default single-hidden" hidden ref={defaultRef} />

        <div class="single-gated-content" ref={contentRef} />

        <section class="single-hidden" hidden ref={loginRef}>
          {!membershipsLoading && !productAccessLoading && (
            <LoginButtons
              accountSettings={accountSettings ?? accountSettingsResp?.accountSettings}
              loginTypes={loginTypes}
              loginRedirectUrl={loginRedirectUrl}
              membershipProduct={membershipProduct}
              gatedProduct={gatedProduct}
              marginTop={0}
            />
          )}
        </section>
      </section>

      {/* Other Sections */}
      <section class="single-hidden" hidden ref={soldOutRef} />
      <section class="single-hidden" hidden ref={alreadyPurchasedRef} />
      <section class="single-hidden" hidden ref={upgradeRef} />
    </section>
  );
};

export const ProductFormWrapperComponent: FunctionComponent<ProductFormWrapperProps> = (props) => {
  return (
    <Provider store={store}>
      <ProductFormWrapperContent {...props} />
    </Provider>
  );
};
